import React, { useContext } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  useLocation,
  useRoutes,
} from "react-router-dom";
import AuthLayout from "../layouts/auth-layout";
import DashboardLayout from "../layouts/dashboard-layout";
import Login from "../pages/auth/login";
import Dashboard from "../pages/dashboard/dashboard";
import ForgotPassword from "../pages/auth/forgot-password";
import UpdatePassword from "../pages/auth/update-password";
import MainLayout from "../layouts/main-layout";
import HolidayList from "../pages/dashboard/holidays/list";
import RoleList from "../pages/dashboard/roles/list";
import UserList from "../pages/dashboard/users/list";
import ServicePartnerList from "../pages/dashboard/data-management/service-partner/list";
import TicketForm from "../pages/dashboard/tickets/form";
import DealerForm from "../pages/dashboard/data-management/dealer/form";
import DealerEngineerMapping from "../pages/dashboard/data-management/dealer/eng-data";
import LogsList from "../pages/dashboard/logs/list";
import ProductRateList from "../pages/dashboard/product-rate/list";
import EntityList from "../pages/dashboard/entity/list";
import NotFound from "../components/NotFound/NotFound";
import Forbidden from "../components/Forbidden/Forbidden";
import { UserContext } from "../context";
import ConfigurationList from "../pages/dashboard/configuration/list";
import DealerListDataTable from "../pages/dashboard/data-management/dealer/list";
import ResetPassword from "../pages/auth/reset-password";
import Ticketlist from "../pages/dashboard/tickets/list";
import ReportsList from "../pages/dashboard/reports/list";
import AttendanceList from "../pages/dashboard/attendance/list";
import EncDecUtility from "../pages/dashboard/utility";

const routes = [
  {
    path: "/login",
    element: (
      <AuthLayout>
        <Login />
      </AuthLayout>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <AuthLayout>
        <ForgotPassword />
      </AuthLayout>
    ),
  },
  {
    path: "/update-password",
    element: (
      <MainLayout>
        <UpdatePassword />
      </MainLayout>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <MainLayout>
        <ResetPassword />
      </MainLayout>
    ),
  },
  {
    path: "/dashboard",
    element: (
      <DashboardLayout>
        <Dashboard />
      </DashboardLayout>
    ),
  },

  {
    path: "/dashboard/holidays",
    element: (
      <DashboardLayout>
        <HolidayList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/users",
    element: (
      <DashboardLayout>
        <UserList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/roles",
    element: (
      <DashboardLayout>
        <RoleList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/dealers",
    element: (
      <DashboardLayout>
        <DealerListDataTable />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/dealers/form",
    element: (
      <DashboardLayout>
        <DealerForm />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/service-partners",
    element: (
      <DashboardLayout>
        <ServicePartnerList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/tickets",
    element: (
      <DashboardLayout>
        <Ticketlist />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/ticket/form",
    element: (
      <DashboardLayout>
        <TicketForm />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/entities",
    element: (
      <DashboardLayout>
        <EntityList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/dealer-engineers",
    element: (
      <DashboardLayout>
        <DealerEngineerMapping />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/logs",
    element: (
      <DashboardLayout>
        <LogsList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/product-rate",
    element: (
      <DashboardLayout>
        <ProductRateList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/configuration",
    element: (
      <DashboardLayout>
        <ConfigurationList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/reports",
    element: (
      <DashboardLayout>
        <ReportsList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/attendance",
    element: (
      <DashboardLayout>
        <AttendanceList />
      </DashboardLayout>
    ),
  },
  {
    path: "/dashboard/utility",
    element: (
      <DashboardLayout>
        <EncDecUtility />
      </DashboardLayout>
    ),
  },
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    path: "*",
    element: (
      <DashboardLayout>
        <NotFound />
      </DashboardLayout>
    ),
  },
  {
    path: "/forbidden",
    element: (
      <DashboardLayout>
        <Forbidden />
      </DashboardLayout>
    ),
  },
];

const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const userData: any = useContext(UserContext);
  const authorizedRoutes =
    userData?.menuHierarchy?.map((item: any) =>
      item?.entityUrl.toLocaleLowerCase()
    ) || [];

  const location = useLocation();
  const currentPath = location.pathname.toLocaleLowerCase();

  // Define excluded paths
  const excludedPaths = [
    "/dashboard/dealers/form",
    "/dashboard/ticket/form",
    "/dashboard/utility",
  ];

  // Check if the current path is excluded
  const isExcluded = excludedPaths.includes(currentPath);

  // Check authorization only for non-excluded paths
  const isAuthorized = isExcluded || authorizedRoutes?.includes(currentPath);

  if (!isAuthorized && authorizedRoutes?.length > 0) {
    return <Navigate to="/forbidden" replace />;
  }

  return element;
};

const AppRoutes: React.FC = () => {
  const element = useRoutes(
    routes.map((route) => ({
      ...route,
      element: route.path.startsWith("/dashboard") ? (
        <ProtectedRoute element={route.element} />
      ) : (
        route.element
      ),
    }))
  );

  return <>{element}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

export default App;
